/**
 * How large each mark is relative to the others, from the one number the host supplies.
 *
 * One function rather than the two call sites doing this themselves, and that is half the reason it
 * exists. The grouped layout reserves a sphere per mark from these and the renderer draws each mark
 * from these, and if the two ever computed it separately the packing's non-overlap guarantee would
 * be a guarantee about numbers nobody can see. They were separate, and marks bled through their own
 * group's boundary from a few hundred components up.
 *
 * The other half is that `weight` has no units. It is whatever quantity the host chose to encode as
 * size - a reference count, an annual cost, a headcount - and those differ by orders of magnitude.
 * Multiplying it by a fixed scale only works while every host happens to send numbers of the same
 * size: calibrated against a reference count of 1 to 50, the first cost field to arrive drew one
 * sphere ten times the width of the whole picture. So nothing here is absolute. These are ratios
 * against the typical mark, and `nodeScale` is the size of the typical mark.
 */

import type { NetworkNode } from '../types';

/**
 * What a weightless node comes out as: not a mark at all.
 *
 * Zero weight is a real and load-bearing case - the app gives every expanded group one, so a group
 * is drawn as a region rather than as a sphere at its own centre - and it has to survive
 * normalisation, which would otherwise pull it up to {@link MINIMUM_RATIO} along with everything
 * else.
 *
 * Zero, and it matters that it is exactly zero. This was a sub-pixel `1e-3` on the theory that the
 * packing needed a positive radius to tell two weightless siblings apart, which was simply wrong -
 * the shell solve divides by the distance between *directions*, and those never coincide. What the
 * fiction did cost was real: the packing reads a positive radius as "there is a mark at this
 * centre to clear", so every reader-invented group of one displaced its only member by more than
 * its own diameter and reserved twice the space it needed.
 */
const NOT_A_MARK = 0;

/**
 * How far from the typical mark a mark may get, in either direction. The floor is also what a mark
 * with no value at all is drawn at.
 *
 * A cap rather than a faithful ratio, because one outlier otherwise decides the whole picture: a
 * single component with a thousand references, or one system carrying most of the budget, draws
 * large enough to cover the estate it is supposed to be part of. Eight is where the largest mark
 * still reads as "much bigger than the rest" while leaving them legible - and it is measured
 * against what the old absolute scale produced for a real estate's busiest hub rather than chosen.
 *
 * The floor is not the reciprocal. Marks below the typical one are the common case rather than the
 * exception, and shrinking them by the same factor the cap allows above would put most of an estate
 * under a pixel.
 */
const MINIMUM_RATIO = 0.4;

const MAXIMUM_RATIO = 8;

/**
 * The typical drawn weight, which every other weight is a ratio of.
 *
 * The median rather than the mean, and over the marks rather than over the nodes. A mean is decided
 * by the same outliers the cap exists to contain - one component with a thousand references pulls
 * it above almost every mark in the estate, and the typical mark then normalises to well under the
 * floor, so the whole picture collapses to the minimum and the outlier is the only thing with a
 * size. Weightless nodes are excluded because they are groups, and the number of groups a reader
 * declared should not change how large a component is drawn.
 */
const toTypicalWeight = (weights: readonly (number | null)[]): number => {
  const measured = weights
    .filter((weight): weight is number => weight !== null && weight > 0)
    .sort((a, b) => a - b);
  return measured.length === 0 ? 1 : measured[Math.floor(measured.length / 2)];
};

/**
 * Size per node, as a multiple of the typical mark, parallel to `nodes`.
 *
 * Square-rooted, so what a viewer actually compares - the disc's area - is linear in the quantity.
 * Using the quantity as a radius directly overstates a large mark by its own factor again, which
 * for a reference count running from 1 to 200 is a mark two hundred times the area it earned.
 */
export const toRelativeNodeSizes = (
  nodes: readonly NetworkNode[]
): number[] => {
  const weights = nodes.map(({ weight }) => weight);
  const typical = toTypicalWeight(weights);

  return weights.map(weight => {
    /* No value for whatever is being measured: the smallest mark, and it took no part in deciding
       what the typical one is. A negative number lands here too - a "net change" field produces
       them and there is no such thing as a negative area. */
    if (weight === null || weight < 0) return MINIMUM_RATIO;
    if (weight === 0) return NOT_A_MARK;

    return Math.min(
      MAXIMUM_RATIO,
      Math.max(MINIMUM_RATIO, Math.sqrt(weight / typical))
    );
  });
};
